import { useEffect, useRef, useState } from "react";
import { Star, Trash2, ChevronLeft, Layers, Megaphone, BellRing, Download, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { http, getDeviceId, errMsg } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { pushStatus, enablePush, disablePush } from "@/lib/push";
import { ALERT_META } from "@/components/weather/AlertsBell";
import { Switch } from "@/components/ui/switch";
import { PARAMS } from "@/lib/params";

const QUICK = [
  { name: "İstanbul", lat: 41.01, lon: 28.98 }, { name: "Ankara", lat: 39.93, lon: 32.86 },
  { name: "İzmir", lat: 38.42, lon: 27.14 }, { name: "Antalya", lat: 36.89, lon: 30.71 },
  { name: "Trabzon", lat: 41.0, lon: 39.72 }, { name: "Erzurum", lat: 39.9, lon: 41.27 },
];
const slug = (s) => s.toLocaleLowerCase("tr").replace(/ı/g, "i").replace(/ş/g, "s").replace(/ğ/g, "g").replace(/ü/g, "u").replace(/ö/g, "o").replace(/ç/g, "c").replace(/[^a-z0-9]+/g, "-");

const PushSection = () => {
  const { t, lang } = useI18n();
  const [status, setStatus] = useState("off");
  const [busy, setBusy] = useState(false);
  useEffect(() => { pushStatus().then(setStatus); }, []);
  if (status === "unsupported") return <div className="text-xs text-slate-500">{t("Bu tarayıcı bildirimleri desteklemiyor")}</div>;
  const toggle = async (on) => {
    setBusy(true);
    try {
      if (on) { await enablePush(lang); setStatus("on"); toast.success(t("Bildirimler açıldı")); }
      else { await disablePush(); setStatus("off"); toast(t("Bildirimler kapatıldı")); }
    } catch (e) { toast.error(e.message === "denied" ? t("Bildirim izni verilmedi") : errMsg(e, t("Bu tarayıcı bildirimleri desteklemiyor"))); }
    setBusy(false);
  };
  const test = () => http.post("/push/test", { device_id: getDeviceId() }).then(() => toast.success(t("Test bildirimi gönderildi"))).catch((e) => toast.error(errMsg(e, "Error")));
  return (
    <>
      <label className="flex items-center justify-between gap-2 text-sm text-slate-300">
        <span className="flex items-center gap-2"><BellRing size={14} className="text-amber-300" />{t("Favorilerim için uyarı al")}</span>
        <Switch data-testid="push-toggle" disabled={busy} checked={status === "on"} onCheckedChange={toggle} />
      </label>
      {status === "on" && <button data-testid="push-test-btn" onClick={test} className="text-xs text-sky-300 hover:underline">{t("Test bildirimi gönder")}</button>}
    </>
  );
};

const AlertPrefs = () => {
  const { t } = useI18n();
  const [types, setTypes] = useState(null);
  const touched = useRef(false);
  useEffect(() => {
    http.get("/alert-prefs", { params: { device_id: getDeviceId() } })
      .then((r) => { if (!touched.current) setTypes(r.data.types); }).catch(() => setTypes((x) => x || []));
  }, []);
  if (!types) return null;
  const toggle = async (k) => {
    touched.current = true;
    const next = types.includes(k) ? types.filter((x) => x !== k) : [...types, k];
    setTypes(next);
    try {
      await http.put("/alert-prefs", { device_id: getDeviceId(), types: next });
      window.dispatchEvent(new Event("alert-prefs-changed"));
    } catch { setTypes(types); toast.error(t("Kaydedilemedi")); }
  };
  return (
    <div data-testid="alert-prefs" className="space-y-1.5">
      <div className="text-xs text-slate-400">{t("Hangi uyarıları almak istiyorsunuz?")}</div>
      <div className="flex flex-wrap gap-1.5">
        {Object.entries(ALERT_META).map(([k, m]) => {
          const on = types.includes(k);
          return (
            <button key={k} data-testid={`alert-pref-${k}`} aria-pressed={on} onClick={() => toggle(k)}
              className={`flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${on ? "border-transparent text-slate-100" : "border-white/10 text-slate-500 line-through"}`}
              style={on ? { background: `${m.color}26`, boxShadow: `inset 0 0 0 1px ${m.color}80` } : undefined}>
              <m.icon size={12} style={{ color: on ? m.color : undefined }} /> {t(m.label)}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const InstallSection = ({ installPrompt, onInstall }) => {
  const { t } = useI18n();
  const standalone = window.matchMedia("(display-mode: standalone)").matches;
  const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
  if (standalone) return null;
  if (installPrompt) return (
    <button data-testid="pwa-install-btn" onClick={onInstall} className="flex w-full items-center justify-center gap-2 rounded-lg bg-sky-400 py-2 text-sm font-bold text-slate-950 hover:bg-sky-300">
      <Download size={14} /> {t("Uygulamayı yükle")}
    </button>
  );
  return ios ? <div data-testid="pwa-ios-hint" className="flex items-center gap-2 text-xs text-slate-400"><Smartphone size={14} />{t("iPhone: Paylaş → Ana Ekrana Ekle")}</div> : null;
};

const Section = ({ title, children }) => (
  <div className="space-y-2">
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{title}</div>
    {children}
  </div>
);

export const SidePanel = ({ paramId, setParamId, layers, setLayers, favorites, onPick, onRemoveFav, onClose, onReport, installPrompt, onInstall, theme, setTheme }) => {
  const { t } = useI18n();
  return (
  <aside data-testid="side-panel" className="glass panel-enter absolute bottom-[190px] left-3 sm:bottom-[120px] top-20 z-[1000] flex w-[260px] flex-col gap-6 overflow-y-auto rounded-2xl p-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 font-heading text-lg font-bold uppercase text-white"><Layers size={18} className="text-sky-400" /> {t("Katmanlar")}</div>
      <button data-testid="side-panel-close" onClick={onClose} className="rounded-full p-1 text-slate-400 hover:bg-white/10 hover:text-white"><ChevronLeft size={18} /></button>
    </div>
    <Section title={t("Parametre")}>
      <div className="grid grid-cols-1 gap-1">
        {PARAMS.map((p) => {
          const Icon = p.icon, active = p.id === paramId;
          return (
            <button key={p.id} data-testid={`param-switch-${p.id}`} onClick={() => setParamId(p.id)}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${active ? "bg-sky-500/15 text-sky-200 ring-1 ring-sky-400/40" : "text-slate-300 hover:bg-white/5"}`}>
              <Icon size={16} className={active ? "text-sky-400" : "text-slate-500"} />
              <span className="flex-1">{t(p.label)}</span>
              <span className="font-mono text-[10px] text-slate-500">{t(p.unit)}</span>
            </button>
          );
        })}
      </div>
    </Section>
    <Section title={t("Görünüm")}>
      {[["values", "Değerler & oklar"], ["reports", "Topluluk raporları"]].map(([k, label]) => (
        <label key={k} className="flex items-center justify-between text-sm text-slate-300">
          {t(label)}
          <Switch data-testid={`layer-toggle-${k}`} checked={layers[k]} onCheckedChange={(v) => setLayers((l) => ({ ...l, [k]: v }))} />
        </label>
      ))}
      <label className="flex items-center justify-between text-sm text-slate-300 sm:hidden">
        {t("Açık tema")}
        <Switch data-testid="side-theme-toggle" checked={theme === "light"} onCheckedChange={(v) => setTheme(v ? "light" : "dark")} />
      </label>
      <button data-testid="side-report-btn" onClick={onReport} className="flex w-full items-center justify-center gap-2 rounded-lg border border-orange-400/30 py-2 text-sm text-orange-200 hover:bg-orange-500/10 md:hidden">
        <Megaphone size={14} /> {t("Yol raporu ekle")}
      </button>
    </Section>
    <Section title={t("Favoriler")}>
      {favorites.length === 0 && <div className="text-xs text-slate-500">{t("Bir noktaya tıklayıp yıldıza basarak favori ekleyin.")}</div>}
      {favorites.map((f) => (
        <div key={f.id} className="group flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-white/5">
          <Star size={14} className="fill-amber-400 text-amber-400" />
          <button data-testid={`favorite-item-${f.id}`} onClick={() => onPick(f)} className="flex-1 truncate text-left text-sm text-slate-200">{f.name}</button>
          <button data-testid={`favorite-remove-${f.id}`} onClick={() => onRemoveFav(f.id)} className="text-slate-500 hover:text-red-400 sm:opacity-0 sm:group-hover:opacity-100"><Trash2 size={14} /></button>
        </div>
      ))}
    </Section>
    <Section title={t("Bildirimler")}>
      <PushSection />
      <AlertPrefs />
      <InstallSection installPrompt={installPrompt} onInstall={onInstall} />
    </Section>
    <Section title={t("Hızlı Erişim")}>
      <div className="flex flex-wrap gap-1.5">
        {QUICK.map((c) => (
          <button key={c.name} data-testid={`favorite-city-item-${slug(c.name)}`} onClick={() => onPick(c)}
            className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-300 transition-colors hover:border-sky-400/50 hover:text-sky-200">{c.name}</button>
        ))}
      </div>
    </Section>
  </aside>
  );
};
